'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaMoneyBill, FaPercentage, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTheme } from 'next-themes';
import { toast } from 'react-hot-toast';
import Chart from 'react-apexcharts';

const CalculatorSection = ({ children, title, icon, className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 ${className} backdrop-blur-sm border border-gray-100 dark:border-gray-700`}
    >
      <div className="flex items-center mb-6">
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
          {icon}
        </div>
        <h2 className="text-2xl font-bold ml-3">{title}</h2>
      </div>
      {children}
    </motion.div>
  );
};

const InputField = ({ icon, placeholder, value, onChange, type = 'number' }) => (
  <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2 backdrop-blur-sm">
    <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded-lg">{icon}</div>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const value = e.target.value.replace(/[^0-9.]/g, '');
        onChange(value);
      }}
      className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900 placeholder-gray-400 dark:placeholder-gray-500"
    />
  </div>
);

const ResultCard = ({ title, value, unit = '' }) => (
  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-2xl font-bold">{value}{unit}</p>
  </div>
);

const MarginCalculator = () => {
  const [costPrice, setCostPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [marginPercentage, setMarginPercentage] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState({});
  const { theme } = useTheme();

  const calculateMargin = () => {
    setError('');
    setLoading(true);

    const cp = parseFloat(costPrice);
    const sp = parseFloat(sellingPrice);
    const mp = parseFloat(marginPercentage);

    if (!cp || (!sp && !mp)) {
      setError('Please fill in cost price and either selling price or margin percentage');
      setLoading(false);
      return;
    }

    if (cp <= 0) {
      setError('Cost price must be greater than 0');
      setLoading(false);
      return;
    }

    let calculatedSP = 0;
    let calculatedMP = 0;

    if (sp && !mp) {
      calculatedMP = ((sp - cp) / cp) * 100;
      calculatedSP = sp;
    } else if (mp && !sp) {
      calculatedSP = cp * (1 + mp / 100);
      calculatedMP = mp;
    }

    const profit = calculatedSP - cp;
    const profitPercentage = (profit / cp) * 100;

    const resultData = {
      sellingPrice: calculatedSP.toFixed(2),
      marginPercentage: calculatedMP.toFixed(2),
      profit: profit.toFixed(2),
      profitPercentage: profitPercentage.toFixed(2),
      costPrice: cp.toFixed(2)
    };

    setChartData({
      options: {
        chart: {
          type: 'pie',
          height: 350,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          }
        },
        colors: ['#4F46E5', '#3B82F6', '#10B981'],
        labels: ['Cost Price', 'Profit', 'Margin'],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          labels: {
            colors: theme === 'dark' ? '#fff' : '#333'
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }
        ]
      },
      series: [
        parseFloat(resultData.costPrice),
        parseFloat(resultData.profit),
        parseFloat(resultData.marginPercentage)
      ]
    });

    setResult(resultData);
    setLoading(false);
  };

  const resetCalculator = () => {
    setCostPrice('');
    setSellingPrice('');
    setMarginPercentage('');
    setResult(null);
    setChartData({});
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Margin Calculator - Calculate Profit Margins</title>
        <meta name="description" content="Calculate profit margins with our advanced calculator. Get accurate results for cost price, selling price, and profit margins." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <CalculatorSection title="Margin Calculator" icon={<FaCalculator className="text-4xl text-blue-500 dark:text-blue-400" />}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900 p-4 rounded-lg mb-4 backdrop-blur-sm">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <InputField
                  icon={<FaMoneyBill className="text-green-500 dark:text-green-400" />}
                  placeholder="Cost Price (₹)"
                  value={costPrice}
                  onChange={setCostPrice}
                />
                <InputField
                  icon={<FaMoneyBill className="text-blue-500 dark:text-blue-400" />}
                  placeholder="Selling Price (₹)"
                  value={sellingPrice}
                  onChange={setSellingPrice}
                />
              </div>
              <div className="space-y-4">
                <InputField
                  icon={<FaPercentage className="text-yellow-500 dark:text-yellow-400" />}
                  placeholder="Margin Percentage (%)"
                  value={marginPercentage}
                  onChange={setMarginPercentage}
                />
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={calculateMargin}
                disabled={loading}
                className="w-full md:w-auto bg-gradient-to-r from-blue-800 to-indigo-500 text-yellow-500 py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:scale-95 disabled:shadow-md flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Calculating...</span>
                  </div>
                ) : (
                  <span>Calculate Margin</span>
                )}
              </button>

              <button
                type="button"
                onClick={resetCalculator}
                className="w-full md:w-auto bg-gradient-to-r from-blue-500 to-blue-700 text-yellow-500 py-3 px-6 rounded-lg hover:from-blue-600 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center"
              >
                <span>Reset Calculator</span>
              </button>
            </div>
          </div>

          {result && (
            <div className="mt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ResultCard title="Cost Price" value={`₹${result.costPrice}`} />
                <ResultCard title="Selling Price" value={`₹${result.sellingPrice}`} />
                <ResultCard title="Margin Percentage" value={`${result.marginPercentage}%`} />
                <ResultCard title="Profit" value={`₹${result.profit}`} />
                <ResultCard title="Profit Percentage" value={`${result.profitPercentage}%`} />
              </div>

              <CalculatorSection title="Price Distribution" icon={<FaChartLine className="text-blue-500 dark:text-blue-400" />} className="p-0">
                <div className="relative h-[300px]">
                  <Chart
                    options={chartData.options}
                    series={chartData.series}
                    type="pie"
                    height="100%"
                  />
                </div>
              </CalculatorSection>

              <CalculatorSection title="Solution Breakdown" icon={<FaInfoCircle className="text-blue-500 dark:text-blue-400" />}>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <FaCalculator className="text-blue-500 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Calculation Method</h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {sellingPrice && !marginPercentage ? 'Calculated Margin Percentage' : 'Calculated Selling Price'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <FaMoneyBill className="text-green-500 dark:text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Profit Calculation</h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        Profit = Selling Price - Cost Price<br />
                        Profit = ₹{result.sellingPrice} - ₹{result.costPrice} = ₹{result.profit}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <FaPercentage className="text-yellow-500 dark:text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Margin Percentage</h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        Margin % = (Profit / Cost Price) × 100<br />
                        Margin % = ({result.profit} / {result.costPrice}) × 100 = {result.marginPercentage}%
                      </p>
                    </div>
                  </div>
                </div>
              </CalculatorSection>
            </div>
          )}
        </CalculatorSection>

        <CalculatorSection title="How to Use" icon={<FaInfoCircle className="text-blue-500 dark:text-blue-400" />} className="mt-12">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <FaInfoCircle className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold">Enter Cost Price</h3>
                <p className="text-gray-600 dark:text-gray-400">Input the cost price of your product.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <FaMoneyBill className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold">Choose Calculation Method</h3>
                <p className="text-gray-600 dark:text-gray-400">Enter either the selling price or margin percentage (not both).</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <FaCalculator className="text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold">Calculate</h3>
                <p className="text-gray-600 dark:text-gray-400">Click the "Calculate Margin" button to see all results.</p>
              </div>
            </div>
          </div>
        </CalculatorSection>

        <CalculatorSection title="Features" icon={<FaChartLine className="text-blue-500 dark:text-blue-400" />} className="mt-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <FaChartLine className="text-blue-500 dark:text-blue-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold">Dual Calculation Mode</h3>
                <p className="text-gray-600 dark:text-gray-400">Calculate either selling price or margin percentage.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <FaMoneyBill className="text-blue-500 dark:text-blue-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold">Profit Analysis</h3>
                <p className="text-gray-600 dark:text-gray-400">Get detailed profit and margin percentages.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <FaPercentage className="text-blue-500 dark:text-blue-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold">Visual Distribution</h3>
                <p className="text-gray-600 dark:text-gray-400">See how your price is distributed between cost, profit, and margin.</p>
              </div>
            </div>
          </div>
        </CalculatorSection>

        <CalculatorSection title="Benefits" icon={<FaCalculator className="text-blue-500 dark:text-blue-400" />} className="mt-8">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaMoneyBill className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Optimize Pricing Strategy</h3>
                <p className="text-gray-600 dark:text-gray-400">Make informed decisions about pricing to maximize profits while remaining competitive.</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaChartLine className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Track Business Performance</h3>
                <p className="text-gray-600 dark:text-gray-400">Monitor your profit margins and business growth over time with clear visual insights.</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaPercentage className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Reduce Financial Risk</h3>
                <p className="text-gray-600 dark:text-gray-400">Avoid underpricing or overpricing by accurately calculating margins and profits.</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaInfoCircle className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Educational Tool</h3>
                <p className="text-gray-600 dark:text-gray-400">Learn about profit margins and pricing strategies through practical examples and calculations.</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaCalculator className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Time-Saving</h3>
                <p className="text-gray-600 dark:text-gray-400">Get instant calculations without manual calculations or spreadsheets.</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-lg">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FaChartLine className="text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">Data-Driven Decisions</h3>
                <p className="text-gray-600 dark:text-gray-400">Make business decisions based on accurate calculations and visual data representation.</p>
              </div>
            </div>
          </div>
        </CalculatorSection>

        <CalculatorSection title="FAQ" icon={<FaInfoCircle className="text-blue-500 dark:text-blue-400" />} className="mt-8">
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
              <h3 className="font-semibold">What is profit margin?</h3>
              <p className="text-gray-600 dark:text-gray-400">Profit margin is the percentage of the selling price that is turned into profit.</p>
            </div>
            <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
              <h3 className="font-semibold">How does this calculator work?</h3>
              <p className="text-gray-600 dark:text-gray-400">It calculates either your selling price or margin percentage based on your cost price and one of the other values.</p>
            </div>
            <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4">
              <h3 className="font-semibold">What does the pie chart show?</h3>
              <p className="text-gray-600 dark:text-gray-400">The pie chart shows the distribution of your total price between cost price, profit, and margin percentage.</p>
            </div>
          </div>
        </CalculatorSection>
      </div>
    </div>
  );
};

export default MarginCalculator;
